import {
  Input,
  Label,
  Picker,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@opero/ui";
import { COUNTRIES, countryCode, matchesCountry } from "@opero/shared";

export interface OrgProfileValues {
  name: string;
  industry: string;
  timezone: string;
  country: string;
}

interface OrgProfileSectionProps {
  values: OrgProfileValues;
  onChange: (next: OrgProfileValues) => void;
}

const KNOWN_INDUSTRIES = [
  "Development Finance",
  "Sovereign Wealth Fund",
  "Venture Capital",
  "Government / Public Sector",
  "Infrastructure",
  "Energy",
  "Agriculture",
  "Healthcare",
  "Education",
];
const OTHER = "Other";

const TIMEZONES = [
  "Africa/Accra",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Africa/Johannesburg",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Dubai",
  "Asia/Singapore",
  "Pacific/Auckland",
];

export function OrgProfileSection({ values, onChange }: OrgProfileSectionProps) {
  const set = <K extends keyof OrgProfileValues>(
    key: K,
    value: OrgProfileValues[K],
  ) => onChange({ ...values, [key]: value });

  const isOther =
    values.industry === OTHER ||
    (values.industry !== "" && !KNOWN_INDUSTRIES.includes(values.industry));
  const selectValue = isOther ? OTHER : values.industry || undefined;

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          Organization
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Tell us about your workspace.
        </p>
      </header>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Workspace name *</Label>
          <Input
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Your organization name"
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Industry</Label>
            <Select
              value={selectValue}
              onValueChange={(v) => set("industry", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select industry..." />
              </SelectTrigger>
              <SelectContent>
                {KNOWN_INDUSTRIES.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
                <SelectItem value={OTHER}>{OTHER}</SelectItem>
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                className="mt-2"
                value={values.industry === OTHER ? "" : values.industry}
                onChange={(e) => set("industry", e.target.value)}
                placeholder="Specify your industry"
              />
            )}
          </div>

          {/* The full ISO 3166-1 list, storing the alpha-2 code the gateway
              keeps and Organization settings reads back. This was a free-text
              input, which is how the same field ended up holding "Ghana" from
              here and "GH" from there. */}
          <div className="space-y-1.5">
            <Label>Country</Label>
            <Picker
              options={COUNTRIES}
              value={countryCode(values.country) ?? ""}
              onChange={(next) =>
                set("country", typeof next === "string" ? next : "")
              }
              clearable
              placeholder="Select country..."
              searchPlaceholder="Search countries..."
              emptyMessage="No countries found."
              filterOption={matchesCountry}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Timezone</Label>
          <Select
            value={values.timezone}
            onValueChange={(v) => set("timezone", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </section>
  );
}
